import { useEffect, useState } from 'react';
import 'pdfjs-dist/legacy/build/pdf.worker.entry';
import { getDocument } from 'pdfjs-dist/legacy/build/pdf';

const DEFAULT_PAGE_SETTINGS = { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const round1 = (v) => Math.round(Number(v || 0) * 10) / 10;

const buildTextLines = (items) => {
  // pdfjs 的 textContent.items 是按绘制顺序/坐标离散分布的，这里做一个“按行聚合”
  // 目标：把同一行上的文本段拼成可检索字符串，并保留每段的 bbox 信息用于定位关键字
  const list = (Array.isArray(items) ? items : [])
    .map((it) => {
      const str = String(it?.str || '');
      const t = Array.isArray(it?.transform) ? it.transform : null;
      const x = Number(t?.[4] || 0);
      const y = Number(t?.[5] || 0);
      const w = Number(it?.width || 0);
      const h = Number(it?.height || 0);
      return { str, x, y, w, h };
    })
    .filter((it) => it.str && Number.isFinite(it.x) && Number.isFinite(it.y) && Number.isFinite(it.w) && Number.isFinite(it.h));

  list.sort((a, b) => {
    const byY = b.y - a.y;
    if (Math.abs(byY) > 0.1) return byY;
    return a.x - b.x;
  });

  const lineThreshold = 2.5;
  const lines = [];
  for (const it of list) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y - it.y) <= lineThreshold) {
      last.items.push(it);
      last.y = (last.y * (last.items.length - 1) + it.y) / last.items.length;
    } else {
      lines.push({ y: it.y, items: [it] });
    }
  }

  for (const ln of lines) {
    ln.items.sort((a, b) => a.x - b.x);
    let s = '';
    let idx = 0;
    ln.runs = ln.items.map((it) => {
      const start = idx;
      s += it.str;
      idx += it.str.length;
      return { item: it, start, end: idx };
    });
    ln.text = s;
  }

  return lines;
};

const findFirstKeywordBboxPx = (lines, keywords) => {
  // 从最后一页往前扫描时，找出第一处命中关键字的 bbox（像素坐标）
  const keys = (Array.isArray(keywords) ? keywords : []).map((k) => String(k || '').trim()).filter(Boolean);
  if (!keys.length) return null;

  for (const ln of lines) {
    const text = String(ln?.text || '');
    if (!text) continue;
    for (const k of keys) {
      const at = text.indexOf(k);
      if (at < 0) continue;
      const start = at;
      const end = at + k.length;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const r of ln.runs || []) {
        const overlap = Math.max(0, Math.min(end, r.end) - Math.max(start, r.start));
        if (overlap <= 0) continue;
        const it = r.item;
        minX = Math.min(minX, it.x);
        minY = Math.min(minY, it.y);
        maxX = Math.max(maxX, it.x + it.w);
        maxY = Math.max(maxY, it.y + it.h);
      }
      if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY) && maxX > minX && maxY > minY) {
        return { x1: minX, y1: minY, x2: maxX, y2: maxY };
      }
    }
  }

  return null;
};

const bboxPxToMmTopOrigin = ({ bboxPx, viewport, pageSettings }) => {
  // 将 pdf 像素坐标转换为 mm，并统一到“以页面左上角为原点”的坐标系
  // 注意：pdfjs 的 viewport y 轴方向与常规页面坐标相反，需要做一次翻转
  const ps = pageSettings || DEFAULT_PAGE_SETTINGS;
  const wPx = Number(viewport?.width || 0);
  const hPx = Number(viewport?.height || 0);
  if (!(wPx > 0 && hPx > 0)) return null;

  const mmPerPxX = Number(ps.widthMm || 210) / wPx;
  const mmPerPxY = Number(ps.heightMm || 297) / hPx;

  const x1 = bboxPx.x1 * mmPerPxX;
  const x2 = bboxPx.x2 * mmPerPxX;
  const yBottom = bboxPx.y1 * mmPerPxY;
  const yTop = bboxPx.y2 * mmPerPxY;

  const top = Number(ps.heightMm || 297) - yTop;
  const bottom = Number(ps.heightMm || 297) - yBottom;

  return { x1, x2, top, bottom };
};

const computeOffsetsFromAnchor = ({ anchorMm, pageSettings, sealWidthMm, sealAspect }) => {
  // 目标：把印章放在关键字附近（靠右侧、垂直居中），并计算“距右/距底”的偏移量（mm）
  // 页面内容区 = 纸张尺寸 - 页边距；偏移量以内容区右下为基准，便于前端预览叠加与交互一致
  const ps = pageSettings || DEFAULT_PAGE_SETTINGS;
  const stampW = Math.max(0, Number(sealWidthMm || 0));
  const stampH = Math.max(0, stampW * Math.max(0, Number(sealAspect || 1)));

  const contentLeft = Number(ps.marginLeftMm || 0);
  const contentRight = Number(ps.widthMm || 210) - Number(ps.marginRightMm || 0);
  const contentTop = Number(ps.marginTopMm || 0);
  const contentBottom = Number(ps.heightMm || 297) - Number(ps.marginBottomMm || 0);

  const contentW = Math.max(0, contentRight - contentLeft);
  const contentH = Math.max(0, contentBottom - contentTop);
  if (!(stampW > 0 && stampH > 0 && contentW > 0 && contentH > 0)) return null;

  const anchorCenterY = (Number(anchorMm.top || 0) + Number(anchorMm.bottom || 0)) / 2;
  const anchorRight = Number(anchorMm.x2 || 0);

  const desiredRightEdge = clamp(anchorRight, contentLeft + stampW, contentRight);
  const stampLeft = clamp(desiredRightEdge - stampW, contentLeft, contentRight - stampW);
  const stampTop = clamp(anchorCenterY - stampH / 2, contentTop, contentBottom - stampH);

  const offsetRight = Math.max(0, contentRight - (stampLeft + stampW));
  const offsetBottom = Math.max(0, contentBottom - (stampTop + stampH));

  return { sealOffsetRightMm: round1(offsetRight), sealOffsetBottomMm: round1(offsetBottom) };
};

const useSmartSealDefaultPosition = ({
  enabled,
  pdfData,
  pageSettings,
  sealWidthMm,
  sealAspect,
  keywords,
}) => {
  const [suggestedOffsets, setSuggestedOffsets] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    // enabled 由外部控制：通常在“已拿到预览 PDF + 已选印章 + 未有历史偏移”时才启用
    if (!enabled || !pdfData) {
      setSuggestedOffsets(null);
      setSuggesting(false);
      return undefined;
    }

    let alive = true;
    setSuggesting(true);
    setSuggestedOffsets(null);

    // 兼容 ArrayBuffer 与 Uint8Array 两类输入，统一转为 pdfjs 可识别的数据
    const bytes = pdfData instanceof ArrayBuffer ? new Uint8Array(pdfData) : pdfData;
    const task = getDocument({ data: bytes });

    (async () => {
      try {
        const doc = await task.promise;
        const ps = pageSettings || DEFAULT_PAGE_SETTINGS;
        const keys = (Array.isArray(keywords) && keywords.length)
          ? keywords
          : ['电子签章', '签章', '印章', '盖章', '签名'];

        // 从最后一页开始找关键字：实际场景中签章落点通常靠近落款位置
        for (let p = Number(doc?.numPages || 0); p >= 1; p -= 1) {
          const page = await doc.getPage(p);
          if (!alive) return;
          const viewport = page.getViewport({ scale: 1 });
          const tc = await page.getTextContent();
          if (!alive) return;
          const lines = buildTextLines(tc?.items || []);
          const bboxPx = findFirstKeywordBboxPx(lines, keys);
          if (!bboxPx) continue;
          const anchorMm = bboxPxToMmTopOrigin({ bboxPx, viewport, pageSettings: ps });
          if (!anchorMm) continue;
          const offsets = computeOffsetsFromAnchor({ anchorMm, pageSettings: ps, sealWidthMm, sealAspect });
          if (!offsets) continue;
          if (!alive) return;
          setSuggestedOffsets(offsets);
          return;
        }
      } catch (e) {
      } finally {
        if (alive) setSuggesting(false);
      }
    })();

    return () => {
      alive = false;
      try { task.destroy(); } catch (e) {}
    };
  }, [enabled, pdfData, pageSettings, sealAspect, sealWidthMm, keywords]);

  return { suggestedOffsets, suggesting };
};

export default useSmartSealDefaultPosition;
